/**
 * The cover image a card shows (`specs/M4-V2-kanban.md` §7.5, `specs/M4-V7-gallery.md` §7.3).
 *
 * It is the **registry's** answer now: `ColumnTypeUi.coverImageOf(value, settings, context)`,
 * implemented by `file` (the first image asset) and `people` / `person` (the photo). Round 5u
 * had to probe the value's *shape* here because `board-view/columns/**` belonged to another
 * worker; round 5x moved the knowledge into the three type folders, so nothing outside a
 * type's folder branches on a type (CLAUDE.md rule 6) and a new type that can show a picture
 * — `board_relation`'s first item, say — gains a cover by implementing one member.
 *
 * What stays here is the part that is the **view's**: which column the *Cover* setting names,
 * and whether the band crops or fits.
 */
import { getColumnUi } from '../../board-view/columns/registry';
import type { CellRenderContext } from '../../board-view/columns/types';
import type { BoardColumn, ItemRow } from '../../board-view/data/types';

export interface CardCover {
  readonly url: string;
  readonly fit: 'cover' | 'contain';
}

/** `true` when this column's type can produce a cover at all — nothing else can be one. */
export function canCover(column: BoardColumn | undefined): boolean {
  if (column === undefined) return false;
  return getColumnUi(column.type).coverImageOf !== undefined;
}

/** The cover for one item, or `null`. `fit` is the view's *Crop* / *Fit* setting. */
export function coverImageFor(
  item: ItemRow,
  column: BoardColumn | undefined,
  context: CellRenderContext,
  displayMode: 'crop' | 'fit' = 'crop',
): CardCover | null {
  if (column === undefined) return null;
  const ui = getColumnUi(column.type);
  if (ui.coverImageOf === undefined) return null;
  const value = column.id === 'name' ? item.name : (item.values[column.id] ?? null);
  const url = ui.coverImageOf(value, column.settings, context);
  if (url === null || url === '') return null;
  return { url, fit: displayMode === 'fit' ? 'contain' : 'cover' };
}

/**
 * The first cover column a `ColumnSelection` names, in board order — skipping any whose type
 * cannot produce a picture, so the card never reserves a 150 px band it will not draw.
 */
export function coverColumnOf(
  columns: readonly BoardColumn[],
  coverColumnIds: readonly string[],
): BoardColumn | undefined {
  return columns.find((column) => coverColumnIds.includes(column.id) && canCover(column));
}
